import React, { useEffect, useRef, useState } from 'react';

interface ParallaxBackgroundProps {
    className?: string;
    intensity?: number; // 1-10 (default 5)
    variant?: 'primary' | 'secondary' | 'dark';
}

const ParallaxBackground: React.FC<ParallaxBackgroundProps> = ({
    className = "",
    intensity = 5,
    variant = 'primary'
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);

        const handleMouseMove = (e: MouseEvent) => {
            if (isMobile) return;

            const { innerWidth, innerHeight } = window;
            // Calculate offset from center (-0.5 to 0.5)
            const x = (e.clientX / innerWidth - 0.5) * (intensity * 10);
            const y = (e.clientY / innerHeight - 0.5) * (intensity * 10);

            setOffset({ x, y });
        };

        // Auto-animation for mobile or idle desktop
        let animationFrameId: number;
        let startTime = Date.now();

        const animate = () => {
            if (isMobile) {
                const time = Date.now() - startTime;
                const x = Math.sin(time / 2000) * (intensity * 5);
                const y = Math.cos(time / 2500) * (intensity * 5);
                setOffset({ x, y });
            }
            animationFrameId = requestAnimationFrame(animate);
        };

        if (!isMobile) {
            window.addEventListener('mousemove', handleMouseMove);
        } else {
            animate();
        }

        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(animationFrameId);
        };
    }, [intensity, isMobile]);

    const getColors = () => {
        switch (variant) {
            case 'secondary':
                return ['rgba(34, 197, 94, 0.15)', 'rgba(21, 128, 61, 0.1)', 'rgba(187, 247, 208, 0.2)'];
            case 'dark':
                return ['rgba(15, 23, 42, 0.05)', 'rgba(30, 41, 59, 0.03)', 'rgba(0, 0, 0, 0.02)'];
            default: // primary
                return ['rgba(16, 185, 129, 0.12)', 'rgba(5, 150, 105, 0.08)', 'rgba(52, 211, 153, 0.15)'];
        }
    };

    const colors = getColors();

    return (
        <div
            ref={containerRef}
            className={`absolute inset-0 overflow-hidden pointer-events-none z-0 ${className}`}
        >
            <div
                className="absolute inset-[-10%] transition-transform duration-75 ease-out will-change-transform"
                style={{
                    transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
                    background: `
            radial-gradient(circle at 20% 30%, ${colors[0]} 0%, transparent 40%),
            radial-gradient(circle at 80% 20%, ${colors[1]} 0%, transparent 40%),
            radial-gradient(circle at 50% 80%, ${colors[2]} 0%, transparent 50%),
            radial-gradient(circle at 10% 90%, ${colors[0]} 0%, transparent 30%)
          `,
                    filter: 'blur(60px)'
                }}
            />
        </div>
    );
};

export default ParallaxBackground;
